/**
 * Utility functions for applying image shape styles to event images
 */

export type ImageShape = 'circle' | 'square' | 'heart' | 'star' | undefined;

export interface ImageShapeStyles {
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Get CSS classes and styles for an image shape
 * @param shape - The shape type ('circle', 'square', 'heart', or undefined for default square)
 * @returns Object with className and style properties
 */
export function getImageShapeStyles(shape: ImageShape): ImageShapeStyles {
  // Default to square if shape is undefined or null
  const effectiveShape = shape || 'square';

  switch (effectiveShape) {
    case 'circle':
      return {
        className: 'rounded-full',
        style: {}
      };

    case 'heart':
      // Smooth heart shape using SVG path with Bezier curves
      // The path uses objectBoundingBox units (0-1) for proper scaling
      return {
        className: '',
        style: {
          clipPath: 'url(#heartClipPath)',
          WebkitClipPath: 'url(#heartClipPath)'
        }
      };

    case 'star':
      // Star shape using polygon - 5-pointed star
      return {
        className: '',
        style: {
          clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)',
          WebkitClipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)'
        }
      };

    case 'square':
    default:
      return {
        className: 'rounded-lg',
        style: {}
      };
  }
}

/**
 * Get container styles for image shape (useful for heart shape wrapper)
 * @param shape - The shape type
 * @returns Object with className and style properties for container
 */
export function getImageShapeContainerStyles(shape: ImageShape): ImageShapeStyles {
  const effectiveShape = shape || 'square';

  // Heart shape may need special container handling
  if (effectiveShape === 'heart') {
    return {
      className: '',
      style: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }
    };
  }

  return {
    className: '',
    style: {}
  };
}

